import {
  addFavoriteProtocols,
  getFavoriteProtocols,
  getRunningProtocol,
  removeFavoriteProtocols,
} from 'api/protocolManagementApi';
import {getDataProtocolsAndFolders} from 'lib/protocolHelper';
import {getAvailableProtocols, selectFavoriteProtocols} from 'stores/selectors/protocolSelectors';
import {getTrayCalibrationTrayID} from 'stores/selectors/trayCalibrationSelectors';
import {protocolExplorerHelper} from 'lib/explorerHelper';
import {
  setCurrentProtocolFolder,
  setProtocolExplorerItems,
  setProtocolExplorerTrayBoundProtocolIDs,
  setProtocolFolderList,
  setSelectedProtocolOrFolder,
} from 'stores/actions/protocolExplorerAction';
import {setAvailableProtocols, setFavoriteProtocols} from 'stores/actions/protocolAction';
import {batch} from 'react-redux';
import {getCurrentProtocolFolder} from 'stores/selectors/protocolExplorerSelectors';
import {parseMessage} from 'lib/stringHelper';
import {hideLoading, showLoading, showSnackbar} from 'stores/actions/appStateAction';
import {Feedback, ServiceResponseCode} from 'constants/dataEnum';
import {selectSessionUserId} from 'stores/selectors/sessionSelectors';
import {ProtocolExplorerAccordionGroup} from 'constants/protocolExplorerEnum';
import {
  clearAndUpdateCurrentRunningProtocol,
  duplicateProtocolsAndFolders,
  fetchProtocolsAndFoldersByFolderId,
  formatProtocolsAndFolders,
  isFolderUpdated,
  updateCurrentRunningProtocol,
} from './protocolManagementBusiness';
import {fetchProtocolBoundOnTray, fetchTrayBarcode} from './trayBusiness';
import {showFolderUpdatedModal, showProtocolOrFolderNotAvailable} from './modalStateBusinessAction';
import {refreshLabwareDefinitions} from './labwareDatabaseBusiness';
import {checkIsInstrumentIdle} from './instrumentStatusBusiness';

const getSelectedItems = (selectedIds) => (dispatch, getState) => {
  const availableProtocols = getAvailableProtocols(getState());
  const favoriteProtocols = selectFavoriteProtocols(getState());

  const allExplorerItems = [...availableProtocols, ...favoriteProtocols];

  const selectedItems = allExplorerItems.filter((protocol) => selectedIds.indexOf(protocol.ID) !== -1);
  return selectedItems;
};

const handleFavoriteClick =
  (selectedItems, isSetAsFavorite = true) =>
  async (dispatch, getState) => {
    const currentFolder = getCurrentProtocolFolder(getState());
    const {protocols} = getDataProtocolsAndFolders(selectedItems);
    const ids = protocols.map((p) => p.id);

    if (isSetAsFavorite) {
      await addFavoriteProtocols(ids);
    } else {
      await removeFavoriteProtocols(ids);
    }

    await dispatch(protocolExplorerBusiness.fetchAndSetExplorerItems(currentFolder?.id));
  };

const fetchAndFormatExplorerItems = (folderId) => async (dispatch, getState) => {
  const trayId = getTrayCalibrationTrayID(getState());

  const allProtocolsResponse = await dispatch(fetchProtocolsAndFoldersByFolderId(folderId));
  const trayProtocolsResponse = await dispatch(fetchProtocolBoundOnTray(folderId, trayId));
  const favoriteProtocolsResponse = await getFavoriteProtocols();

  const favoriteProtocols = protocolExplorerHelper.formatFavoriteProtocols(favoriteProtocolsResponse);
  const favoriteProtocolIds = favoriteProtocolsResponse.map((protocol) => protocol.id);

  const [protocolsByTray] = dispatch(formatProtocolsAndFolders(trayProtocolsResponse));
  const [allProtocolList, newProtocolFolderList] = dispatch(
    formatProtocolsAndFolders(allProtocolsResponse, favoriteProtocolIds),
  );

  const explorerItems = [...(allProtocolList || []), ...favoriteProtocols];

  return {explorerItems, allProtocolList, newProtocolFolderList, favoriteProtocols, protocolsByTray};
};

const fetchAndSetExplorerItems = (folderId) => async (dispatch) => {
  const isOnRoot = Boolean(!folderId);
  const fetchResults = await dispatch(fetchAndFormatExplorerItems(folderId));
  const {explorerItems, allProtocolList, newProtocolFolderList, favoriteProtocols, protocolsByTray} = fetchResults;
  const trayBoundProtocolIds = protocolsByTray ? protocolsByTray.map((item) => item.ID) : [];

  if (!allProtocolList) {
    return null;
  }

  // dont display favorite protocols when not in root
  const explorerItemsToSet = isOnRoot ? explorerItems : allProtocolList;
  const favoriteProtocolsToSet = isOnRoot ? favoriteProtocols : [];

  batch(() => {
    dispatch(setProtocolExplorerItems(explorerItemsToSet));
    dispatch(setAvailableProtocols(allProtocolList));
    dispatch(setProtocolFolderList(newProtocolFolderList));
    dispatch(setFavoriteProtocols(favoriteProtocolsToSet));
    dispatch(setProtocolExplorerTrayBoundProtocolIDs(trayBoundProtocolIds));
  });

  return {...fetchResults, explorerItems: explorerItemsToSet, favoriteProtocols: favoriteProtocolsToSet};
};

const validateRefetechedExplorerItems = (fetchResults) => async (dispatch) => {
  const {allProtocolList, newProtocolFolderList} = fetchResults || {};

  if (!fetchResults) {
    const callback = () => dispatch(setCurrentProtocolFolder({}));
    dispatch(showProtocolOrFolderNotAvailable(callback));
    return;
  }

  const isFolderDataUpdated = await dispatch(isFolderUpdated(allProtocolList, newProtocolFolderList));

  if (isFolderDataUpdated) {
    dispatch(showFolderUpdatedModal());
  }
};

const handleExplorerInstrumentChecking = () => async (dispatch) => {
  const isIdle = await dispatch(checkIsInstrumentIdle());
  const response = await getRunningProtocol();
  const runningProtocol = response.payload;

  const isNeedToClearState = runningProtocol && isIdle;

  if (!isIdle) {
    await dispatch(updateCurrentRunningProtocol(runningProtocol));
  } else if (isNeedToClearState) {
    await dispatch(clearAndUpdateCurrentRunningProtocol());
  }
};

const reloadInstrumentCondition = () => async (dispatch) => {
  await dispatch(fetchTrayBarcode());
  await dispatch(refreshLabwareDefinitions());
  await dispatch(handleExplorerInstrumentChecking());
};

const handleDuplicateProtocol = (selectedItems) => async (dispatch, getState) => {
  const currentFolder = getCurrentProtocolFolder(getState());
  const userId = selectSessionUserId(getState());

  const type = protocolExplorerHelper.getSelectedItemsUniformType(selectedItems);
  const {protocols, folders} = getDataProtocolsAndFolders(selectedItems);

  const protocolsOnFavoriteProtocolsGroup = selectedItems.filter(
    (protocol) => protocol.groupView === ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS,
  );
  const favoriteProtocolIds = protocolsOnFavoriteProtocolsGroup.map((protocol) => protocol.protocol.id);

  const loadingMessage = parseMessage('explorer.explorerMenuBarContainer.duplicateProtocol.loadingMessage', {type});

  dispatch(showLoading(loadingMessage));

  const response = await dispatch(duplicateProtocolsAndFolders(protocols, folders, favoriteProtocolIds));

  dispatch(hideLoading());

  if (response.code !== ServiceResponseCode.OK) {
    if (response.code === ServiceResponseCode.UNAUTH_ACTION_BECAUSE_NOT_TESSARA) {
      const snackbarMessage = parseMessage('explorer.download.error.tessaraInsideFolder');

      dispatch(showSnackbar(Feedback.ERROR, snackbarMessage));
    }

    // protocol name max characters exceeded
    if (response.code === ServiceResponseCode.PROTOCOL_NAME_MAX_CHARACTERS_EXCEEDED) {
      const snackbarMessage = parseMessage('explorer.modal.rename.warning.modalMaxCharacters');

      dispatch(showSnackbar(Feedback.ERROR, snackbarMessage));
    }

    return;
  }

  const newProtocols = response.payload?.protocols || [];
  const newFolders = response.payload?.protocolFolders || [];

  const newProtocolIds = [...newProtocols, ...newFolders].map((e) => e.id);

  const isCurrentlyOnSharedFolder = currentFolder && currentFolder.owner !== userId;

  const duplicateSuccessMessageId = isCurrentlyOnSharedFolder
    ? 'explorer.explorerMenuBarContainer.duplicateSharedItem.successMessage'
    : 'explorer.explorerMenuBarContainer.duplicateProtocol.successMessage';

  const snackbarMessage = parseMessage(duplicateSuccessMessageId, {
    protocolCount: newProtocolIds.length,
    contentType: type,
  });

  batch(() => {
    dispatch(setSelectedProtocolOrFolder(newProtocolIds));
    dispatch(fetchAndSetExplorerItems(currentFolder?.id));
    dispatch(showSnackbar(Feedback.SUCCESS, snackbarMessage));
  });
};

export const protocolExplorerBusiness = {
  fetchAndFormatExplorerItems,
  fetchAndSetExplorerItems,
  validateRefetechedExplorerItems,
  reloadInstrumentCondition,
  getSelectedItems,
};

export const protocolMenuHandler = {
  handleDuplicateProtocol,
  handleFavoriteClick,
};

export default protocolExplorerBusiness;
